<template>
    <div style="width: 100%">
        <v-text-field
            label="Période comptable"
            variant="outlined"
            readonly
            hide-details
            :class="fullTextFieldClass"
            @click="showPeriodSelector = true"
            :disabled="disabled"
            :prepend-inner-icon="
                global
                    ? selectedAccountingPeriod
                        ? '$oss-circle-check'
                        : '$oss-circle-uncheck'
                    : ''
            "
            :append-inner-icon="
                selectedAccountingPeriod &&
                (selectedAccountingPeriod.closed ||
                    selectedAccountingPeriod.type === 'AccountingClosing')
                    ? '$lock_outline'
                    : ''
            "
            @click:prepend-inner="onPrependClick"
            data-cy="ossAccountingPeriodSelector-period"
            :model-value="titleName"
            density="comfortable"
        />

        <oss-dialog v-model="showPeriodSelector" :title="title">
            <div
                :style="{
                    width: '50em',
                }"
            >
                <v-tabs
                    v-model="step"
                    fixed-tabs
                    style="
                        background-color: transparent !important;
                        border-radius: 16px;
                        overflow: hidden;
                    "
                >
                    <v-tab value="periods" href="#periods"> <strong>Exercices</strong></v-tab>
                    <v-tab value="closings" href="#closings"><strong>Clôtures</strong></v-tab>
                </v-tabs>

                <v-tabs-window v-model="step">
                    <!-- ** ACCOUNTING PERIOD STEP -->
                    <v-window-item value="periods">
                        <v-list
                            style="overflow-y: auto; height: 50vh"
                            :model-value="accountingPeriod ? accountingPeriod.id : ''"
                        >
                            <template v-for="period in accountingPeriods" :key="period.id">
                                <v-list-item
                                    @click="selectPeriod(period)"
                                    rounded="xl"
                                    class="mb-2"
                                    lines="two"
                                    border
                                    v-if="
                                        (!period.accountingPeriod ||
                                            period.accountingPeriod.end !== period.end) &&
                                        period.type !== 'AccountingClosing'
                                    "
                                    :data-cy="'ossAccountingPeriodSelector-' + period.id"
                                    :model-value="period.id"
                                    :title="getName(period)"
                                    :subtitle="period.name ? getPeriodString(period) : ''"
                                >
                                    <template #append>
                                        <v-icon
                                            start
                                            v-if="
                                                period.type === 'AccountingClosing' || period.closed
                                            "
                                            >$lock_outline</v-icon
                                        >
                                        <v-icon end color="grey lighten-1" size="x-small"
                                            >$fa-chevron-right</v-icon
                                        >
                                    </template>
                                </v-list-item>
                                <template
                                    v-if="
                                        period.accountingSituations &&
                                        period.accountingSituations.length
                                    "
                                >
                                    <v-list-item
                                        v-for="accountingSituation in period.accountingSituations"
                                        :key="accountingSituation.id"
                                        rounded="xl"
                                        class="mb-2"
                                        lines="two"
                                        border
                                        :value="accountingSituation.id"
                                        @click="selectPeriod(accountingSituation)"
                                        :data-cy="
                                            'ossAccountingPeriodSelector-' + accountingSituation.id
                                        "
                                        :title="getAccountingSituationName(accountingSituation)"
                                    >
                                        <template #append>
                                            <v-icon>$subdirectory_arrow_right</v-icon>
                                        </template>
                                        <template #prepend>
                                            <v-icon
                                                start
                                                v-if="
                                                    period.type === 'AccountingClosing' ||
                                                    period.closed
                                                "
                                                >$lock_outline</v-icon
                                            >
                                            <v-icon end color="grey lighten-1" size="x-small"
                                                >$fa-chevron-right</v-icon
                                            >
                                        </template>
                                    </v-list-item>
                                </template>
                            </template>
                        </v-list>
                    </v-window-item>
                    <!-- ** ACCOUNTING CLOSING STEP -->
                    <v-window-item value="closings">
                        <v-list
                            style="overflow-y: auto; height: 50vh"
                            :model-value="accountingPeriod ? accountingPeriod.id : ''"
                        >
                            <template v-for="period in accountingPeriods">
                                <v-list-item
                                    :key="period.id"
                                    :value="period.id"
                                    @click="selectPeriod(period)"
                                    v-if="
                                        !period.accountingPeriod ||
                                        period.accountingPeriod.end !== period.end
                                    "
                                    :data-cy="'ossAccountingPeriodSelector-' + period.id"
                                    :title="getName(period)"
                                    rounded="xl"
                                    class="mb-2"
                                    lines="two"
                                    border
                                    :subtitle="period.name ? getPeriodString(period) : ''"
                                >
                                    <template #prepend v-if="period.type === 'AccountingClosing'">
                                        <v-icon>$subdirectory_arrow_right</v-icon>
                                    </template>
                                    <template #append>
                                        <v-icon
                                            v-if="
                                                period.type === 'AccountingClosing' || period.closed
                                            "
                                            start
                                            >$lock_outline</v-icon
                                        >
                                        <v-icon end color="grey lighten-1" size="x-small"
                                            >$fa-chevron-right</v-icon
                                        >
                                    </template>
                                </v-list-item>
                            </template>
                        </v-list>
                    </v-window-item>
                </v-tabs-window>
            </div>
        </oss-dialog>
    </div>
</template>

<script lang="ts">
import moment from "moment-timezone";
import _ from "lodash";
import { State2Way } from "@/decorators";

import ACCOUNTING_PERIODS from "../../graphql/accountingPeriod/AccountingPeriods.gql";

import { Apollo } from "@/decorators";
import { Component, Vue, Model, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

@Component
export class ossAccountingPeriodSelector extends Vue {
    @Prop({ default: "" }) companyId: string;
    @Prop({ default: "Exercice comptable" }) label: string;
    @Prop({ default: false }) dense: boolean;
    @Prop({ default: "DD MMMM YYYY" }) formatDate: string;
    @Prop({ default: false }) disabled: boolean;
    @Prop({ default: false }) hideDetails: string;
    @Prop({ default: false }) block: boolean;
    @Prop({ default: undefined }) placeholder: string;
    @Prop({ default: "" }) selectClass: string;
    @Prop({ default: false }) global: boolean;
    @Prop({ default: false }) withAccountingClosing: boolean;
    @Model() value;

    showPeriodSelector = false;

    step = "periods";

    @Watch("companyId")
    onCompanyChanged() {
        this.accountingPeriods = [];
        if (this.global) {
            this.accountingPeriodsStore = [];
        }
        this.accountingPeriod = null;
        this.accountingClosing = null;
    }

    @State2Way("updateAccountingPeriods", "accountingPeriod.accountingPeriods")
    accountingPeriodsStore!: [];

    @State2Way("updateSelectedAccountingPeriod", "accountingPeriod.selectedAccountingPeriod")
    selectedAccountingPeriod!: any;

    @State2Way(
        "updateLastSelectedAccountingPeriod",
        "accountingPeriod.lastSelectedAccountingPeriod",
    )
    lastSelectedAccountingPeriod!: any;

    @State2Way("updateAccountingPeriodReadOnly", "accountingPeriod.readonly")
    readonly!: any;

    @State2Way("updateSelectedAccountingClosing", "accountingPeriod.selectedAccountingClosing")
    selectedAccountingClosing!: any;

    //
    // period
    //

    accountingPeriod_: any = null;

    get titleName() {
        if (this.accountingClosing) {
            return this.accountingClosing.name;
        }
        return this.getName(this.accountingPeriod);
    }

    get accountingPeriod() {
        if (this.global) {
            return this.selectedAccountingPeriod;
        }
        if (this.accountingPeriod_) {
            return this.accountingPeriod_;
        }
        // if (this.value) {
        return this.value;
        // }
    }

    set accountingPeriod(value) {
        if (this.global) {
            this.selectedAccountingPeriod = value;
        } else {
            this.accountingPeriod_ = value;
            this.value = value;
        }
    }

    //
    // closing
    //
    get accountingClosing() {
        if (this.global) {
            return this.selectedAccountingClosing;
        }
        return null;
    }

    set accountingClosing(value) {
        if (this.global) {
            this.selectedAccountingClosing = value;
        }
        // debugger
    }

    selectPeriod(period) {
        if (period.type === "AccountingClosing") {
            this.accountingClosing = period;
            this.accountingPeriod = period.accountingPeriod;
        } else {
            this.accountingPeriod = period;
            this.accountingClosing = null;
        }
        this.showPeriodSelector = false;
    }

    getName(accountingPeriod) {
        if (!accountingPeriod) {
            return "";
        }
        if (accountingPeriod?.name) {
            return accountingPeriod.name;
        }
        return this.getPeriodString(accountingPeriod);
    }

    getPeriodString(accountingPeriod) {
        try {
            const start = moment(accountingPeriod.start).format(this.formatDate);
            const end = moment(accountingPeriod.end).format(this.formatDate);
            return "Du " + start + " au " + end;
        } catch (e) {
            return "";
        }
    }

    getAccountingSituationName(accountingSituation) {
        return (
            "Situation du " +
            moment(accountingSituation.start).format("DD/MM/YYYY") +
            " au " +
            moment(accountingSituation.end).format("DD/MM/YYYY")
        );
    }

    // @Apollo({
    //     query: ACCOUNTING_PERIODS,
    //     variables() {
    //         return {
    //             companyId: this.companyId
    //         }
    //     },
    //     skip() {
    //         return this.companyId === ""
    //     },
    //     specificResult({ data, loading, networkStatus }: any) {
    //         if (!loading) {
    //             if (data && data.accountingPeriods) {
    //                 if (this.global) {
    //                     this.accountingPeriodsStore = data.accountingPeriods
    //                 } else {
    //                     this.accountingPeriods_ = data.accountingPeriods
    //                     let accountingPeriod = this.accountingPeriod
    //                     if (accountingPeriod?.id) {
    //                         if (!this.accountingPeriods_.find(ap => ap.id === this.accountingPeriod.id)) {
    //                             accountingPeriod = null
    //                         }
    //                     }
    //                     if (!accountingPeriod?.id) {
    //                         if (this.accountingPeriods_.length) {
    //                             accountingPeriod = data.accountingPeriods[data.accountingPeriods.length-1]
    //                         }
    //                     }
    //                     this.accountingPeriod = accountingPeriod
    //                 }

    //             } else {
    //                 if (this.global) {
    //                     this.accountingPeriodsStore = []
    //                 } else {
    //                     this.accountingPeriods_ = []
    //                 }
    //                 this.accountingPeriod = null
    //             }
    //         }
    //     }
    // })
    // accountingPeriods_ = []

    @Apollo({
        query: ACCOUNTING_PERIODS,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return this.companyId === "";
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.accountingPeriods) {
                    if (this.global) {
                        this.accountingPeriodsStore = data.accountingPeriods;
                    } else {
                        this.accountingPeriods_ = data.accountingPeriods;
                        let accountingPeriod = this.accountingPeriod;
                        if (accountingPeriod?.id) {
                            if (
                                !this.accountingPeriods_.find(
                                    (ap) => ap.id === this.accountingPeriod.id,
                                )
                            ) {
                                accountingPeriod = null;
                            }
                        }
                        if (!accountingPeriod?.id) {
                            if (this.accountingPeriods_.length) {
                                accountingPeriod =
                                    data.accountingPeriods[data.accountingPeriods.length - 1];
                            }
                        }
                        this.accountingPeriod = accountingPeriod;
                    }
                } else {
                    if (this.global) {
                        this.accountingPeriodsStore = [];
                    } else {
                        this.accountingPeriods_ = [];
                    }
                    this.accountingPeriod = null;
                }
            }
        },
    })
    accountingPeriods_ = [];

    accountingClosings_ = [];

    get accountingPeriods() {
        const accountingPeriods = _.cloneDeep(
            this.global ? this.accountingPeriodsStore : this.accountingPeriods_,
        );
        if (this.withAccountingClosing) {
            return accountingPeriods
                .sort((a, b) => {
                    return a.start < b.start ? 1 : -1;
                })
                .flatMap((ap) => {
                    let { accountingClosings, ...accountingPeriod } = ap;
                    accountingClosings = accountingClosings
                        .map((ac) => {
                            return {
                                ...ac,
                                start: ap.start,
                                end: ac.endDate,
                                type: "AccountingClosing",
                                accountingPeriod: ap,
                            };
                        })
                        .sort((a, b) => {
                            return a.end < b.end ? -1 : 1;
                        });
                    // debugger
                    return [
                        { ...accountingPeriod, type: "AccountingPeriod" },
                        ...accountingClosings,
                    ];
                });
        }
        return accountingPeriods.map((ap) => ({ ...ap, type: "AccountingPeriod" }));
    }

    set accountingPeriods(data) {
        this.accountingPeriods_ = data;
    }

    get title() {
        if (this.accountingPeriods_?.some((x) => x.type === "AccountingClosing")) {
            return "Sélectionnez un exercice comptable ou une situation";
        }
        return "Sélectionnez un exercice comptable";
    }

    onPrependClick() {
        if (this.global) {
            if (!this.$store.state.accountingPeriod.isMandatory) {
                if (this.$store.state.accountingPeriod.lastSelectedAccountingClosing) {
                    if (!this.selectedAccountingClosing) {
                        this.selectedAccountingClosing =
                            this.$store.state.accountingPeriod.lastSelectedAccountingClosing;
                    } else {
                        this.selectedAccountingClosing = null;
                    }
                }
                if (this.selectedAccountingPeriod) {
                    this.selectedAccountingPeriod = null;
                } else {
                    this.selectedAccountingPeriod = this.lastSelectedAccountingPeriod;
                }
            }
        }
    }

    get fullTextFieldClass() {
        let tfClass = "small-prepend-icon";
        if (this.selectClass) {
            tfClass += " " + this.selectClass;
        }
        return tfClass;
    }

    get accountingClosings() {
        if (this.selectedAccountingPeriod?.accountingClosings?.length) {
            const accountingClosings = [
                {
                    id: "",
                    name: "Sans situation",
                },
            ];
            accountingClosings.push(...this.selectedAccountingPeriod.accountingClosings);
            return accountingClosings;
        }
        return [];
    }
}

export default toNative(ossAccountingPeriodSelector);
</script>
